/**
 * "N items not imported" — the honest half of the import report.
 *
 * Nothing listed here is lost: every entry is stored verbatim in
 * `hangarmax_import_links.raw_source` and re-evaluated on every re-sync, so
 * items become importable as PlaneMAX grows the matching capability.
 */

import { AlertTriangle } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'

export interface UnmatchedEntry {
  label: string
  reason: string
}

interface UnmatchedItemsListProps {
  items: UnmatchedEntry[]
  /** Compact mode drops the explanatory footer (used in the result report). */
  compact?: boolean
}

export function UnmatchedItemsList({ items, compact = false }: UnmatchedItemsListProps) {
  if (items.length === 0) return null

  // Group identical reasons so a layout with 40 GLTF tugs reads as one line.
  const grouped = new Map<string, string[]>()
  for (const item of items) {
    const bucket = grouped.get(item.reason) ?? []
    bucket.push(item.label)
    grouped.set(item.reason, bucket)
  }

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
      <div className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-900 dark:text-amber-200">
        <AlertTriangle className="h-4 w-4" />
        {items.length} item{items.length === 1 ? '' : 's'} not imported
      </div>

      <ScrollArea className="max-h-48">
        <ul className="space-y-2 px-3 pb-3">
          {Array.from(grouped.entries()).map(([reason, labels]) => (
            <li key={reason} className="text-xs text-amber-900/90 dark:text-amber-200/90">
              <div className="font-medium">{reason}</div>
              <div className="text-amber-800/70 dark:text-amber-300/70">
                {labels.slice(0, 12).join(', ')}
                {labels.length > 12 ? ` +${labels.length - 12} more` : ''}
              </div>
            </li>
          ))}
        </ul>
      </ScrollArea>

      {!compact && (
        <p className="border-t border-amber-200 px-3 py-2 text-xs text-amber-800/80 dark:border-amber-900 dark:text-amber-300/80">
          These are stored with the import and re-checked every time you re-sync,
          so they will come in automatically once PlaneMAX can represent them.
        </p>
      )}
    </div>
  )
}
